/**
 * useMiniappIconSpec — resolve a mini-app folder's custom launcher icon spec by
 * id, kept live via the `file:updated` realtime event.
 *
 * Why a hook (not a row field): breadcrumb crumbs carry only `{id, name}` — the
 * path-cache tree that feeds them is deliberately lean (it holds EVERY file for
 * a tenant; see `features/files/core/path-cache.ts`), so threading
 * `metadata_json` through it would bloat a hot cache. Instead the crumb fetches
 * the spec on demand: the query is DISABLED for any non-mini-app name (zero
 * network for ordinary folders) and TanStack-deduped/cached across every crumb
 * and tab, so a path with one mini-app ancestor costs at most one GET.
 *
 * The `file:updated` subscription mirrors the taskbar dock chip
 * (`App.tsx` → `setAppIcon`) and the drive-row tile patch, so editing the icon
 * (from the dialog, the agent tool, or another tab) updates the breadcrumb glyph
 * live without a reload.
 *
 * Returns null (→ the caller falls back to `defaultIconSpecFor(name)`) for any
 * non-mini-app name or before the fetch resolves.
 */
import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isMiniappFolder, parseMiniappIcon, parseMiniappIconRev, type MiniappIconSpec } from '@papan/types'
import { miniappApi } from '@papan/files/client'
import { useRealtimeOptional } from '@papan/realtime/client'

/** Query-cache key for a mini-app folder's resolved icon spec. */
export const miniappIconQueryKey = (fileId: string) => ['miniappIcon', fileId] as const

/** The spec AND its cache-bust rev. Both are needed: with the spec alone an IMAGE
 *  icon can't load its composited PNG and quietly renders the gradient default. */
export interface MiniappIconResolved {
  spec: MiniappIconSpec | null
  rev: string | null
}

export function useMiniappIconSpec(fileId: string, name: string): MiniappIconResolved {
  const isApp = isMiniappFolder(name)
  const qc = useQueryClient()
  const realtime = useRealtimeOptional()

  const { data } = useQuery<MiniappIconResolved>({
    queryKey: miniappIconQueryKey(fileId),
    queryFn: async () => {
      const { icon, rev } = await miniappApi.getIcon(fileId)
      return { spec: icon, rev: rev ?? null }
    },
    enabled: isApp,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!isApp || !realtime) return
    return realtime.onFileEvent((event) => {
      if (event.type === 'file:updated' && event.fileId === fileId && event.file) {
        const md = event.file.metadataJson ?? null
        // Patch BOTH — a replaced image changes only the rev, so dropping it here
        // would pin the crumb to the old composited PNG.
        qc.setQueryData(miniappIconQueryKey(fileId), { spec: parseMiniappIcon(md), rev: parseMiniappIconRev(md) })
      }
    })
  }, [isApp, fileId, realtime, qc])

  return isApp ? (data ?? { spec: null, rev: null }) : { spec: null, rev: null }
}
